<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { Editor } from '@tiptap/core';
    import StarterKit from '@tiptap/starter-kit';
    import Link from '@tiptap/extension-link';
    import Typography from '@tiptap/extension-typography';
    import Placeholder from '@tiptap/extension-placeholder';

    let {
        value = '',
        placeholder = '',
        extended = false,
        onchange,
    }: {
        value: string;
        placeholder?: string;
        extended?: boolean;
        onchange: (html: string) => void;
    } = $props();

    let editorElement: HTMLDivElement;
    let editor: Editor | null = $state(null);
    let editorRevision = $state(0);
    let linkEditing = $state(false);
    let linkText = $state('');
    let linkUrl = $state('');

    onMount(() => {
        editor = new Editor({
            element: editorElement,
            extensions: [
                StarterKit.configure({
                    gapcursor: extended ? {} : false,
                }),
                Link.configure({
                    autolink: true,
                    openOnClick: false,
                    linkOnPaste: true,
                    enableClickSelection: true,
                    HTMLAttributes: { class: 'text-cyan-400 underline' },
                    isAllowedUri: (url, ctx) => {
                        return ctx.defaultValidate(url) && /^https?:\/\/.+/.test(url);
                    },
                }),
                Typography,
                Placeholder.configure({
                    placeholder,
                }),
            ],
            content: value,
            editorProps: {
                attributes: {
                    class: 'prose prose-sm prose-invert max-w-none focus:outline-none min-h-[6rem] px-3 py-2',
                },
            },
            onUpdate: ({ editor: e }) => {
                onchange(e.isEmpty ? '' : e.getHTML());
            },
            onTransaction: () => {
                editorRevision++;
            },
        });
    });

    onDestroy(() => {
        editor?.destroy();
    });

    function openLinkEditor(): void {
        if (!editor) return;
        const { from, to } = editor.state.selection;
        linkText = editor.state.doc.textBetween(from, to, '');
        linkUrl = editor.getAttributes('link').href ?? '';
        linkEditing = true;
    }

    function saveLinkEdit(): void {
        if (!editor) return;
        const { from, to } = editor.state.selection;
        const currentText = editor.state.doc.textBetween(from, to, '');

        if (!linkUrl) {
            editor.chain().focus().unsetLink().run();
        } else if (linkText !== currentText) {
            editor.chain().focus()
                .deleteRange({ from, to })
                .insertContent(`<a href="${linkUrl}">${linkText}</a>`)
                .run();
        } else {
            editor.chain().focus().setLink({ href: linkUrl }).run();
        }
        linkEditing = false;
    }

    function cancelLinkEdit(): void {
        linkEditing = false;
        editor?.chain().focus().run();
    }

    function removeLink(): void {
        if (!editor) return;
        editor.chain().focus().unsetLink().run();
    }

    function isActive(name: string, attrs?: Record<string, unknown>): boolean {
        editorRevision;
        if (!editor?.isFocused) return false;
        return editor.isActive(name, attrs);
    }

    type ToolbarButton = {
        label: string;
        icon: { viewBox: string; path: string } | string;
        action: () => void;
        activeName: string | null;
        activeAttrs?: Record<string, unknown>;
        enabledWhen?: string;
    };

    const headingButtons: ToolbarButton[] = [
        {
            label: 'Heading 1',
            icon: 'H1',
            action: () => editor?.chain().focus().toggleHeading({ level: 1 }).run(),
            activeName: 'heading',
            activeAttrs: { level: 1 },
        },
        {
            label: 'Heading 2',
            icon: 'H2',
            action: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
            activeName: 'heading',
            activeAttrs: { level: 2 },
        },
    ];

    const baseButtons: ToolbarButton[] = [
        {
            label: 'Bold',
            icon: { viewBox: '0 0 384 512', path: 'M32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l32 0 0 320-32 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l224 0c70.7 0 128-57.3 128-128 0-46.5-24.8-87.3-62-109.7 18.7-22.3 30-51 30-82.3 0-70.7-57.3-128-128-128L32 32zM288 160c0 35.3-28.7 64-64 64l-96 0 0-128 96 0c35.3 0 64 28.7 64 64zM128 416l0-128 128 0c35.3 0 64 28.7 64 64s-28.7 64-64 64l-128 0z' },
            action: () => editor?.chain().focus().toggleBold().run(),
            activeName: 'bold',
        },
        {
            label: 'Italic',
            icon: { viewBox: '0 0 384 512', path: 'M128 64c0-17.7 14.3-32 32-32l192 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-58.7 0-133.3 320 64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 480c-17.7 0-32-14.3-32-32s14.3-32 32-32l58.7 0 133.3-320-64 0c-17.7 0-32-14.3-32-32z' },
            action: () => editor?.chain().focus().toggleItalic().run(),
            activeName: 'italic',
        },
        {
            label: 'Bullet List',
            icon: { viewBox: '0 0 512 512', path: 'M48 144a48 48 0 1 0 0-96 48 48 0 1 0 0 96zM192 64c-17.7 0-32 14.3-32 32s14.3 32 32 32l288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L192 64zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-288 0zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-288 0zM48 464a48 48 0 1 0 0-96 48 48 0 1 0 0 96zM96 256a48 48 0 1 0 -96 0 48 48 0 1 0 96 0z' },
            action: () => editor?.chain().focus().toggleBulletList().run(),
            activeName: 'bulletList',
        },
        {
            label: 'Ordered List',
            icon: { viewBox: '0 0 512 512', path: 'M0 72C0 58.8 10.7 48 24 48l48 0c13.3 0 24 10.7 24 24l0 104 24 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-96 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l24 0 0-80-24 0C10.7 96 0 85.3 0 72zM30.4 301.2C41.8 292.6 55.7 288 70 288l4.9 0c33.7 0 61.1 27.4 61.1 61.1 0 19.6-9.4 37.9-25.2 49.4l-24 17.5 33.2 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-90.7 0C13.1 464 0 450.9 0 434.7 0 425.3 4.5 416.5 12.1 411l70.5-51.3c3.4-2.5 5.4-6.4 5.4-10.6 0-7.2-5.9-13.1-13.1-13.1L70 336c-3.9 0-7.7 1.3-10.8 3.6L38.4 355.2c-10.6 8-25.6 5.8-33.6-4.8S-1 324.8 9.6 316.8l20.8-15.6zM224 64l256 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-256 0c-17.7 0-32-14.3-32-32s14.3-32 32-32zm0 160l256 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-256 0c-17.7 0-32-14.3-32-32s14.3-32 32-32zm0 160l256 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-256 0c-17.7 0-32-14.3-32-32s14.3-32 32-32z' },
            action: () => editor?.chain().focus().toggleOrderedList().run(),
            activeName: 'orderedList',
        },
        {
            label: 'Link',
            icon: { viewBox: '0 0 576 512', path: 'M419.5 96c-16.6 0-32.7 4.5-46.8 12.7-15.8-16-34.2-29.4-54.5-39.5 28.2-24 64.1-37.2 101.3-37.2 86.4 0 156.5 70 156.5 156.5 0 41.5-16.5 81.3-45.8 110.6l-71.1 71.1c-29.3 29.3-69.1 45.8-110.6 45.8-86.4 0-156.5-70-156.5-156.5 0-1.5 0-3 .1-4.5 .5-17.7 15.2-31.6 32.9-31.1s31.6 15.2 31.1 32.9c0 .9 0 1.8 0 2.6 0 51.1 41.4 92.5 92.5 92.5 24.5 0 48-9.7 65.4-27.1l71.1-71.1c17.3-17.3 27.1-40.9 27.1-65.4 0-51.1-41.4-92.5-92.5-92.5zM275.2 173.3c-1.9-.8-3.8-1.9-5.5-3.1-12.6-6.5-27-10.2-42.1-10.2-24.5 0-48 9.7-65.4 27.1L91.1 258.2c-17.3 17.3-27.1 40.9-27.1 65.4 0 51.1 41.4 92.5 92.5 92.5 16.5 0 32.6-4.4 46.7-12.6 15.8 16 34.2 29.4 54.6 39.5-28.2 23.9-64 37.2-101.3 37.2-86.4 0-156.5-70-156.5-156.5 0-41.5 16.5-81.3 45.8-110.6l71.1-71.1c29.3-29.3 69.1-45.8 110.6-45.8 86.6 0 156.5 70.6 156.5 156.9 0 1.3 0 2.6 0 3.9-.4 17.7-15.1 31.6-32.8 31.2s-31.6-15.1-31.2-32.8c0-.8 0-1.5 0-2.3 0-33.7-18-63.3-44.8-79.6z' },
            action: openLinkEditor,
            activeName: 'link',
        },
        {
            label: 'Unlink',
            icon: { viewBox: '0 0 576 512', path: 'M41-24.9c-9.4-9.4-24.6-9.4-33.9 0S-2.3-.3 7 9.1l528 528c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-122-122c4.2-3.4 8.3-7.1 12.1-10.9l71.1-71.1c29.3-29.3 45.8-69.1 45.8-110.6 0-86.4-70-156.5-156.5-156.5-37.3 0-73.1 13.3-101.3 37.2 20.3 10.1 38.7 23.5 54.5 39.5 14.1-8.3 30.2-12.7 46.8-12.7 51.1 0 92.5 41.4 92.5 92.5 0 24.5-9.7 48-27.1 65.4l-71.1 71.1c-3.9 3.9-8.1 7.4-12.6 10.5l-47.5-47.5c16.5-.9 29.7-14.4 30.2-31.1 0-1.3 0-2.6 0-3.9 0-86.3-69.9-156.9-156.5-156.9-19.2 0-37.9 3.5-55.5 10.2L41-24.9zM225.9 160c.6 0 1.1 0 1.7 0 15.1 0 29.5 3.7 42.1 10.2 1.8 1.2 3.6 2.3 5.5 3.1 26.8 16.3 44.8 45.9 44.8 79.6 0 .4 0 .8 0 1.2L225.9 160zM346.2 416L192 261.8c1.2 84.6 69.6 152.9 154.1 154.1zM139.7 209.5l-45.3-45.3-48.6 48.6c-29.3 29.3-45.8 69.1-45.8 110.6 0 86.4 70 156.5 156.5 156.5 37.2 0 73.1-13.3 101.3-37.2-20.3-10.1-38.8-23.5-54.6-39.5-14 8.2-30.1 12.6-46.7 12.6-51.1 0-92.5-41.4-92.5-92.5 0-24.5 9.7-48 27.1-65.4l48.6-48.6z' },
            action: removeLink,
            activeName: null,
            enabledWhen: 'link',
        },
        {
            label: 'Undo',
            icon: { viewBox: '0 0 512 512', path: 'M24 192l144 0c9.7 0 18.5-5.8 22.2-14.8s1.7-19.3-5.2-26.2l-46.7-46.7c75.3-58.6 184.3-53.3 253.5 15.9 75 75 75 196.5 0 271.5s-196.5 75-271.5 0c-10.2-10.2-19-21.3-26.4-33-9.5-14.9-29.3-19.3-44.2-9.8s-19.3 29.3-9.8 44.2C49.7 408.7 61.4 423.5 75 437 175 537 337 537 437 437S537 175 437 75C342.8-19.3 193.3-24.7 92.7 58.8L41 7C34.1 .2 23.8-1.9 14.8 1.8S0 14.3 0 24L0 168c0 13.3 10.7 24 24 24z' },
            action: () => editor?.chain().focus().undo().run(),
            activeName: null,
        },
        {
            label: 'Redo',
            icon: { viewBox: '0 0 512 512', path: 'M488 192l-144 0c-9.7 0-18.5-5.8-22.2-14.8s-1.7-19.3 5.2-26.2l46.7-46.7c-75.3-58.6-184.3-53.3-253.5 15.9-75 75-75 196.5 0 271.5s196.5 75 271.5 0c8.2-8.2 15.5-16.9 21.9-26.1 10.1-14.5 30.1-18 44.6-7.9s18 30.1 7.9 44.6c-8.5 12.2-18.2 23.8-29.1 34.7-100 100-262.1 100-362 0S-25 175 75 75c94.3-94.3 243.7-99.6 344.3-16.2L471 7c6.9-6.9 17.2-8.9 26.2-5.2S512 14.3 512 24l0 144c0 13.3-10.7 24-24 24z' },
            action: () => editor?.chain().focus().redo().run(),
            activeName: null,
        },
    ];

    const buttons = $derived(extended ? [...headingButtons, ...baseButtons] : baseButtons);
</script>

<div class="rich-text-editor border border-gray-700 rounded focus-within:ring-2 focus-within:ring-cyan-500">
    <div class="flex items-center gap-0.5 px-2 py-1 border-b border-gray-700 bg-gray-800 rounded-t">
        {#each buttons as btn (btn.label)}
            {@const disabled = btn.enabledWhen ? !isActive(btn.enabledWhen) : false}
            {@const active = btn.activeName ? isActive(btn.activeName, btn.activeAttrs) : false}
            <button
                type="button"
                title={btn.label}
                onclick={btn.action}
                {disabled}
                class="p-1.5 rounded transition-colors {disabled ? 'text-gray-600 cursor-not-allowed' : active ? 'bg-gray-700 text-cyan-400' : 'text-gray-400 hover:bg-gray-700'}"
            >
                {#if typeof btn.icon === 'string'}
                    <span class="text-xs font-bold leading-none w-3.5 h-3.5 flex items-center justify-center">{btn.icon}</span>
                {:else}
                    <svg class="w-3.5 h-3.5 fill-current" viewBox={btn.icon.viewBox} xmlns="http://www.w3.org/2000/svg">
                        <path d={btn.icon.path} />
                    </svg>
                {/if}
            </button>
        {/each}
    </div>

    {#if linkEditing}
        <div class="flex items-center gap-2 px-3 py-2 border-b border-gray-700 bg-cyan-950 text-sm">
            <label class="text-gray-400 shrink-0">Text</label>
            <input
                bind:value={linkText}
                type="text"
                class="flex-1 border border-gray-600 bg-gray-800 text-gray-100 rounded px-2 py-1 text-sm min-w-0"
            />
            <label class="text-gray-400 shrink-0">URL</label>
            <input
                bind:value={linkUrl}
                type="url"
                placeholder="https://..."
                class="flex-1 border border-gray-600 bg-gray-800 text-gray-100 rounded px-2 py-1 text-sm min-w-0"
                onkeydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveLinkEdit(); } }}
            />
            <button type="button" onclick={saveLinkEdit} class="text-cyan-400 hover:text-cyan-300 font-medium shrink-0">Save</button>
            <button type="button" onclick={cancelLinkEdit} class="text-gray-500 hover:text-gray-300 shrink-0">Cancel</button>
        </div>
    {/if}

    <div bind:this={editorElement}></div>
</div>

<style>
    :global(.rich-text-editor .tiptap) {
        outline: none;
    }
    :global(.rich-text-editor .tiptap ul) {
        list-style-type: disc;
        padding-left: 1.5rem;
    }
    :global(.rich-text-editor .tiptap ol) {
        list-style-type: decimal;
        padding-left: 1.5rem;
    }
    :global(.rich-text-editor .tiptap h1) {
        font-size: 1.5rem;
        font-weight: 700;
        margin: 0.5rem 0 0.25rem;
    }
    :global(.rich-text-editor .tiptap h2) {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0.5rem 0 0.25rem;
    }
    :global(.rich-text-editor .tiptap p) {
        margin: 0.25rem 0;
    }
    :global(.rich-text-editor .tiptap p.is-editor-empty:first-child::before) {
        content: attr(data-placeholder);
        float: left;
        color: #6b7280;
        pointer-events: none;
        height: 0;
    }
</style>
